import { useNavigate } from 'react-router-dom';

// P2b-3 Build View commit 7 — §2.11: Prev / Next pair shared between
// the header + footer. Wires the location.state.filteredRowIds list
// passed at navigation time. Disabled rules:
//
//   - List missing (hard refresh / deep link)  →  both buttons
//     disabled, tooltip "Return to grid for navigation."
//   - Index 0  →  Prev disabled, tooltip "First in list"
//   - Index === length - 1  →  Next disabled, tooltip "Last in list"
//
// Click navigates to /rollouts/:id/grid/<platform>/<newRowId>/build
// preserving the same filteredRowIds in state so subsequent Prev/Next
// presses keep working.

type Props = {
  rolloutId: string;
  rowId: string;
  platform: 'meta' | 'google';
  filteredRowIds: string[] | null;
};

export function BuildViewNavBar({ rolloutId, rowId, platform, filteredRowIds }: Props) {
  const navigate = useNavigate();

  const hasList = !!filteredRowIds && filteredRowIds.length > 0;
  const idx = hasList ? filteredRowIds!.indexOf(rowId) : -1;
  const total = filteredRowIds?.length ?? 0;
  const prevDisabled = !hasList || idx <= 0;
  const nextDisabled = !hasList || idx === -1 || idx >= total - 1;

  const prevTooltip = !hasList
    ? 'Return to grid for navigation.'
    : idx <= 0
      ? 'First in list'
      : 'Previous row';
  const nextTooltip = !hasList
    ? 'Return to grid for navigation.'
    : idx === -1
      ? 'Row not in current filter'
      : idx >= total - 1
        ? 'Last in list'
        : 'Next row';

  const goTo = (delta: -1 | 1) => {
    if (!hasList || idx === -1) return;
    const targetIdx = idx + delta;
    if (targetIdx < 0 || targetIdx >= total) return;
    const targetRowId = filteredRowIds![targetIdx];
    if (!targetRowId) return;
    navigate(`/rollouts/${rolloutId}/grid/${platform}/${targetRowId}/build`, {
      state: { filteredRowIds },
    });
    // UX fix: advancing to the Next location should land at the top of the
    // new page. The Next button sits in the footer, so without this the
    // next location loads scrolled to the bottom and the AM has to scroll
    // up before they can start. Only on Next (delta === 1) — Prev is left
    // as-is. Instant (behavior 'auto'), no animation — the AM wants the
    // location ready to work on immediately.
    if (delta === 1) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        disabled={prevDisabled}
        onClick={() => goTo(-1)}
        title={prevTooltip}
        aria-label={prevTooltip}
        className="px-2 py-1 text-xs rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        ← Prev
      </button>
      <button
        type="button"
        disabled={nextDisabled}
        onClick={() => goTo(1)}
        title={nextTooltip}
        aria-label={nextTooltip}
        className="px-2 py-1 text-xs rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Next →
      </button>
    </div>
  );
}
